import { useCallback, useEffect, useState } from "react";
import MicrosoftMailConnection from "./MicrosoftMailConnection";
import { Plug } from "lucide-react";
import { Link } from "react-router-dom";
import { xeroApi } from "../../backend/api/xero";
import type { XeroStatus } from "../../backend/api/xero";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Page, PageHeader } from "../../components/patterns";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

export default function Integrations() {
  const [status, setStatus] = useState<XeroStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const result = await xeroApi.status();
    setLoading(false);

    if (result.error) {
      setError(result.error.message || "Could not load connections.");
      return;
    }

    setError("");
    setStatus(result.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connection = status?.connections?.[0];

  const description = loading
    ? "Checking connection..."
    : connection
      ? `${connection.tenant_name}: ${connection.status.replaceAll("_", " ")}`
      : status?.configured
        ? "Connect Xero to the portal."
        : "Xero is not configured for this environment.";

  return (
    <Page width="full">
      <PageHeader
        title="Connections"
        description="Connect the services used by your organisation."
      />

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <MicrosoftMailConnection />

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted">
              <Plug className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <CardTitle className="text-base">Xero</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>

          {status?.can_manage ? <Button asChild variant="outline">
            <Link to="/integrations/xero/settings">{connection ? "Manage" : "Connect Xero"}</Link>
          </Button> : <p className="text-sm text-muted-foreground">Xero is managed by an Owner.</p>}
        </CardHeader>
      </Card>
    </Page>
  );
}
